package com.shadowings.gero.presentation.component.quiz

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.TextAutoSize
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.em
import com.shadowings.gero.domain.model.Characters
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.component.avatars.AlynAvatar
import com.shadowings.gero.presentation.component.avatars.InteractiveAvatar
import com.shadowings.gero.presentation.component.avatars.MaskoAvatar
import com.shadowings.gero.presentation.component.avatars.NiloAvatar
import com.shadowings.gero.presentation.component.avatars.ZetaAvatar
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.DeviceInfo
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import com.shadowings.gero.util.systemLanguageCode
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.correct_answer_prefix
import gero.composeapp.generated.resources.triangle
import gero.composeapp.generated.resources.triangle90
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

/**
 * Renders "$prefix: $correctAnswer" (per [Res.string.correct_answer_prefix]), but when
 * [isHieroglyph] is true, the answer itself is drawn with [MdCWord] instead of as plain text
 * (a Gardiner-code word like "D21" reads as garbage otherwise).
 */
@Composable
private fun CorrectAnswerPrefixText(
    correctAnswer: String,
    isHieroglyph: Boolean,
    style: TextStyle,
    color: Color,
    modifier: Modifier = Modifier,
    textAlign: TextAlign? = null,
) {
    if (isHieroglyph) {
        val sentinel = "@@ANSWER@@"
        val parts = stringResource(Res.string.correct_answer_prefix, sentinel).split(sentinel)
        val prefix = parts.getOrElse(0) { "" }
        val suffix = parts.getOrElse(1) { "" }
        Row(
            modifier = modifier,
            horizontalArrangement = if (textAlign == TextAlign.Center) Arrangement.Center else Arrangement.Start,
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (prefix.isNotBlank()) {
                Text(prefix, style = style, color = color)
                Spacer(Modifier.width(4.dp))
            }
            MdCWord(word = correctAnswer, rtl = false, cart = false, size = 24.dp)
            if (suffix.isNotBlank()) {
                Spacer(Modifier.width(4.dp))
                Text(suffix, style = style, color = color)
            }
        }
    } else {
        Text(
            modifier = modifier,
            text = stringResource(Res.string.correct_answer_prefix, correctAnswer),
            style = style,
            color = color,
            textAlign = textAlign ?: TextAlign.Unspecified
        )
    }
}

/** Same as [CorrectAnswerPrefixText] but for the "($correctAnswer)" style used by some avatars. */
@Composable
private fun CorrectAnswerParenText(
    correctAnswer: String,
    isHieroglyph: Boolean,
    style: TextStyle,
    color: Color,
    modifier: Modifier = Modifier,
    textAlign: TextAlign? = null,
) {
    if (isHieroglyph) {
        Row(
            modifier = modifier,
            horizontalArrangement = if (textAlign == TextAlign.Center) Arrangement.Center else Arrangement.Start,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text("(", style = style, color = color)
            MdCWord(word = correctAnswer, rtl = false, cart = false, size = 24.dp)
            Text(")", style = style, color = color)
        }
    } else {
        Text(
            modifier = modifier,
            text = "($correctAnswer)",
            style = style,
            color = color,
            textAlign = textAlign ?: TextAlign.Unspecified
        )
    }
}

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun AlynFeedbackBoxHappyPreview() {
    AppTheme {
        Surface {
            AlynFeedbackBox(
                Faces.Happy,
                text = "Ottimo lavoro!",
                userData = UserData()
            )
        }
    }
}

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun AlynFeedbackBoxSadPreview() {
    AppTheme {
        Surface {
            AlynFeedbackBox(
                Faces.Sad,
                text = "Oh no! Forse dovresti rileggere le lezioni!",
                userData = UserData()
            )
        }
    }
}

@Composable
fun AlynFeedbackBox(
    face: Faces,
    text: String,
    userData: UserData,
    modifier: Modifier = Modifier,
    correctAnswer: String? = null,
    correctAnswerIsHieroglyph: Boolean = false,
    isLandscape: Boolean = false,
    interactive: Boolean = false
) {
    val deviceInfo = rememberDeviceInfo()
    val landscape = isLandscape || deviceInfo.isLandscape
    val avatarWidth = when (deviceInfo.deviceClass) {
        DeviceClass.PHONE_SMALL -> 64.dp
        DeviceClass.PHONE_MEDIUM -> 72.dp
        else -> if (landscape) 72.dp else 96.dp
    }
    val isPhoneSmallWrong = deviceInfo.deviceClass == DeviceClass.PHONE_SMALL && !correctAnswer.isNullOrBlank()

    val textStyle = if (deviceInfo.deviceClass == DeviceClass.PHONE_SMALL) {
        MaterialTheme.typography.bodyMedium.copy(lineHeight = 1.3.em)
    } else {
        MaterialTheme.typography.bodyLarge.copy(lineHeight = 1.3.em)
    }

    if (landscape) {
        Row(
            modifier = modifier
                .fillMaxWidth()
                .padding(if (isLandscape) 8.dp else 12.dp)
                .semantics { testTag = "feedback_box" },
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (interactive) {
                InteractiveAvatar(
                    character = Characters.Alyn,
                    userData = userData,
                    baseFace = face,
                    modifier = Modifier.height(avatarWidth)
                )
            } else {
                AlynAvatar(
                    userData = userData,
                    face = face,
                )
            }
            Spacer(modifier = Modifier.width(8.dp))
            Image(
                modifier = Modifier.width(12.dp),
                painter = painterResource(Res.drawable.triangle90),
                contentDescription = null,
                colorFilter = ColorFilter.tint(MaterialTheme.colorScheme.outlineVariant)
            )
            OutlinedCard(
                modifier = Modifier.weight(1f)
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(12.dp)
                ) {
                    if (!isPhoneSmallWrong) {
                        Text(
                            modifier = Modifier
                                .semantics { testTag = "feedback_box_text" },
                            text = text,
                            style = textStyle,
                            autoSize = TextAutoSize.StepBased(
                                maxFontSize = textStyle.fontSize
                            ),
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                    if (!correctAnswer.isNullOrBlank()) {
                        CorrectAnswerPrefixText(
                            correctAnswer = correctAnswer,
                            isHieroglyph = correctAnswerIsHieroglyph,
                            modifier = Modifier.padding(top = if (isPhoneSmallWrong) 0.dp else 4.dp),
                            style = MaterialTheme.typography.bodySmall.copy(
                                fontWeight = FontWeight.Bold,
                                lineHeight = 1.2.em
                            ),
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                        )
                    }
                }
            }
        }
    } else {
        Column(
            modifier = modifier
                .fillMaxWidth()
                .padding(horizontal = 8.dp, vertical = 8.dp)
                .semantics { testTag = "feedback_box" },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            if (interactive) {
                InteractiveAvatar(
                    character = Characters.Alyn,
                    userData = userData,
                    baseFace = face,
                    modifier = Modifier.height(avatarWidth)
                )
            } else {
                AlynAvatar(
                    userData = userData,
                    face = face
                )
            }
            Spacer(modifier = Modifier.height(6.dp))
            Image(
                modifier = Modifier.width(14.dp),
                painter = painterResource(Res.drawable.triangle),
                contentDescription = null,
                colorFilter = ColorFilter.tint(MaterialTheme.colorScheme.outlineVariant)
            )
            OutlinedCard(
                modifier = Modifier.fillMaxWidth()
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(12.dp)
                ) {
                    if (!isPhoneSmallWrong) {
                        Text(
                            modifier = Modifier
                                .fillMaxWidth()
                                .semantics { testTag = "feedback_box_text" },
                            text = text,
                            style = textStyle,
                            autoSize = TextAutoSize.StepBased(
                                maxFontSize = textStyle.fontSize
                            ),
                            textAlign = TextAlign.Center,
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                    if (!correctAnswer.isNullOrBlank()) {
                        CorrectAnswerPrefixText(
                            correctAnswer = correctAnswer,
                            isHieroglyph = correctAnswerIsHieroglyph,
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(top = if (isPhoneSmallWrong) 0.dp else 4.dp),
                            style = MaterialTheme.typography.bodyMedium.copy(
                                fontWeight = FontWeight.Bold,
                                lineHeight = 1.2.em
                            ),
                            textAlign = TextAlign.Center,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun ZetaFeedbackBox(
    face: Faces,
    text: String,
    userData: UserData,
    correctAnswer: String? = null,
    correctAnswerIsHieroglyph: Boolean = false,
    isLandscape: Boolean = false
) {
    val deviceInfo = rememberDeviceInfo()
    val landscape = isLandscape || deviceInfo.isLandscape
    val avatarWidth = when (deviceInfo.deviceClass) {
        DeviceClass.PHONE_SMALL -> 64.dp
        DeviceClass.PHONE_MEDIUM -> 72.dp
        else -> if (landscape) 72.dp else 96.dp
    }
    val isPhoneSmallWrong = deviceInfo.deviceClass == DeviceClass.PHONE_SMALL && !correctAnswer.isNullOrBlank()

    if (landscape) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(if (isLandscape) 8.dp else 16.dp)
                .semantics { testTag = "feedback_box" },
            verticalAlignment = Alignment.CenterVertically
        ) {
            ZetaAvatar(
                userData = userData,
                face = face,
                modifier = Modifier
                    .width(avatarWidth)
                    .aspectRatio(1f)
            )
            Spacer(modifier = Modifier.width(8.dp))
            Image(
                modifier = Modifier.width(12.dp),
                painter = painterResource(Res.drawable.triangle90),
                contentDescription = null,
                colorFilter = ColorFilter.tint(MaterialTheme.colorScheme.outlineVariant)
            )
            OutlinedCard(
                modifier = Modifier.weight(1f)
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(12.dp)
                ) {
                    if (!isPhoneSmallWrong) {
                        Text(
                            modifier = Modifier
                                .semantics { testTag = "feedback_box_text" },
                            text = text,
                            style = MaterialTheme.typography.titleMedium.copy(
                                lineHeight = 1.2.em
                            ),
                            autoSize = TextAutoSize.StepBased(
                                maxFontSize = MaterialTheme.typography.titleMedium.fontSize
                            ),
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                    if (!correctAnswer.isNullOrBlank()) {
                        CorrectAnswerPrefixText(
                            correctAnswer = correctAnswer,
                            isHieroglyph = correctAnswerIsHieroglyph,
                            modifier = Modifier.padding(top = if (isPhoneSmallWrong) 0.dp else 4.dp),
                            style = MaterialTheme.typography.bodySmall.copy(
                                fontWeight = FontWeight.Bold,
                                lineHeight = 1.2.em
                            ),
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                        )
                    }
                }
            }
        }
    } else {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
                .semantics { testTag = "feedback_box" },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            ZetaAvatar(
                userData = userData,
                face = face,
                modifier = Modifier
                    .width(avatarWidth)
                    .aspectRatio(1f)
            )
            Spacer(modifier = Modifier.height(8.dp))
            Image(
                modifier = Modifier.width(16.dp),
                painter = painterResource(Res.drawable.triangle),
                contentDescription = null,
                colorFilter = ColorFilter.tint(MaterialTheme.colorScheme.outlineVariant)
            )
            OutlinedCard(
                modifier = Modifier.fillMaxWidth()
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(if (isPhoneSmallWrong) 8.dp else 16.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    Text(
                        modifier = Modifier
                            .semantics { testTag = "feedback_box_text" },
                        text = text,
                        style = MaterialTheme.typography.titleMedium.copy(
                            lineHeight = 1.2.em
                        ),
                        autoSize = TextAutoSize.StepBased(
                            maxFontSize = MaterialTheme.typography.titleMedium.fontSize
                        ),
                        textAlign = TextAlign.Center,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                    if (!correctAnswer.isNullOrBlank()) {
                        CorrectAnswerParenText(
                            correctAnswer = correctAnswer,
                            isHieroglyph = correctAnswerIsHieroglyph,
                            modifier = Modifier
                                .padding(top = if (isPhoneSmallWrong) 0.dp else 4.dp),
                            style = MaterialTheme.typography.bodyMedium.copy(
                                fontWeight = FontWeight.Bold,
                                lineHeight = 1.2.em
                            ),
                            textAlign = TextAlign.Center,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun NiloFeedbackBox(
    text: String,
    userData: UserData,
    face: Faces = Faces.Idle,
    correctAnswer: String? = null,
    correctAnswerIsHieroglyph: Boolean = false,
    isLandscape: Boolean = false,
    useFullText: Boolean = false
) {
    val languageCode = userData.language.ifBlank { systemLanguageCode() }
    val displayMinText = if (useFullText || text.startsWith("Miao", ignoreCase = true)) text else {
        localizedMeowWord(languageCode)
    }
    val deviceInfo = rememberDeviceInfo()
    val landscape = isLandscape || deviceInfo.isLandscape
    val avatarWidth = when (deviceInfo.deviceClass) {
        DeviceClass.PHONE_SMALL -> 64.dp
        DeviceClass.PHONE_MEDIUM -> 72.dp
        else -> if (landscape) 72.dp else 96.dp
    }
    val isPhoneSmallWrong = deviceInfo.deviceClass == DeviceClass.PHONE_SMALL && !correctAnswer.isNullOrBlank()

    if (landscape) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(if (isLandscape) 8.dp else 16.dp)
                .semantics { testTag = "feedback_box" },
            verticalAlignment = Alignment.CenterVertically
        ) {
            NiloAvatar(
                userData = userData,
                face = face,
                modifier = Modifier
                    .width(avatarWidth)
                    .aspectRatio(1f)
            )
            Spacer(modifier = Modifier.width(8.dp))
            Image(
                modifier = Modifier.width(12.dp),
                painter = painterResource(Res.drawable.triangle90),
                contentDescription = null,
                colorFilter = ColorFilter.tint(MaterialTheme.colorScheme.outlineVariant)
            )
            OutlinedCard(
                modifier = Modifier.weight(1f)
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(12.dp)
                ) {
                    if (!isPhoneSmallWrong) {
                        Text(
                            modifier = Modifier
                                .semantics { testTag = "feedback_box_text" },
                            text = displayMinText,
                            style = MaterialTheme.typography.titleMedium.copy(
                                lineHeight = 1.2.em
                            ),
                            autoSize = TextAutoSize.StepBased(
                                maxFontSize = MaterialTheme.typography.titleMedium.fontSize
                            ),
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                    if (!correctAnswer.isNullOrBlank()) {
                        CorrectAnswerParenText(
                            correctAnswer = correctAnswer,
                            isHieroglyph = correctAnswerIsHieroglyph,
                            modifier = Modifier.padding(top = if (isPhoneSmallWrong) 0.dp else 4.dp),
                            style = MaterialTheme.typography.bodySmall.copy(
                                fontWeight = FontWeight.Bold,
                                lineHeight = 1.2.em
                            ),
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                        )
                    }
                }
            }
        }
    } else {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
                .semantics { testTag = "feedback_box" },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            NiloAvatar(
                userData = userData,
                face = face,
                modifier = Modifier
                    .width(avatarWidth)
                    .aspectRatio(1f)
            )
            Spacer(modifier = Modifier.height(8.dp))
            Image(
                modifier = Modifier.width(16.dp),
                painter = painterResource(Res.drawable.triangle),
                contentDescription = null,
                colorFilter = ColorFilter.tint(MaterialTheme.colorScheme.outlineVariant)
            )
            OutlinedCard(
                modifier = Modifier.fillMaxWidth()
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(16.dp)
                ) {
                    if (!isPhoneSmallWrong) {
                        Text(
                            modifier = Modifier
                                .fillMaxWidth()
                                .semantics { testTag = "feedback_box_text" },
                            text = displayMinText,
                            style = MaterialTheme.typography.titleLarge.copy(
                                lineHeight = 1.2.em
                            ),
                            autoSize = TextAutoSize.StepBased(
                                maxFontSize = MaterialTheme.typography.titleLarge.fontSize
                            ),
                            textAlign = TextAlign.Center,
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                    if (!correctAnswer.isNullOrBlank()) {
                        CorrectAnswerParenText(
                            correctAnswer = correctAnswer,
                            isHieroglyph = correctAnswerIsHieroglyph,
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(top = if (isPhoneSmallWrong) 0.dp else 4.dp),
                            style = MaterialTheme.typography.bodyMedium.copy(
                                fontWeight = FontWeight.Bold,
                                lineHeight = 1.2.em
                            ),
                            textAlign = TextAlign.Center,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun MaskoFeedbackBox(
    face: Faces,
    text: String,
    userData: UserData,
    correctAnswer: String? = null,
    correctAnswerIsHieroglyph: Boolean = false,
    isLandscape: Boolean = false
) {
    val deviceInfo = rememberDeviceInfo()
    val landscape = isLandscape || deviceInfo.isLandscape
    val avatarWidth = when (deviceInfo.deviceClass) {
        DeviceClass.PHONE_SMALL -> 64.dp
        DeviceClass.PHONE_MEDIUM -> 72.dp
        else -> if (landscape) 72.dp else 96.dp
    }
    val isPhoneSmallWrong = deviceInfo.deviceClass == DeviceClass.PHONE_SMALL && !correctAnswer.isNullOrBlank()

    if (landscape) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(if (isLandscape) 8.dp else 16.dp)
                .semantics { testTag = "feedback_box" },
            verticalAlignment = Alignment.CenterVertically
        ) {
            MaskoAvatar(
                userData = userData,
                face = face,
                modifier = Modifier
                    .width(avatarWidth)
                    .aspectRatio(1f)
            )
            Spacer(modifier = Modifier.width(8.dp))
            Image(
                modifier = Modifier.width(12.dp),
                painter = painterResource(Res.drawable.triangle90),
                contentDescription = null,
                colorFilter = ColorFilter.tint(MaterialTheme.colorScheme.outlineVariant)
            )
            OutlinedCard(
                modifier = Modifier.weight(1f)
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(12.dp)
                ) {
                    if (!isPhoneSmallWrong) {
                        Text(
                            modifier = Modifier
                                .semantics { testTag = "feedback_box_text" },
                            text = text,
                            style = MaterialTheme.typography.titleMedium.copy(
                                lineHeight = 1.2.em
                            ),
                            autoSize = TextAutoSize.StepBased(
                                maxFontSize = MaterialTheme.typography.titleMedium.fontSize
                            ),
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                    if (!correctAnswer.isNullOrBlank()) {
                        CorrectAnswerPrefixText(
                            correctAnswer = correctAnswer,
                            isHieroglyph = correctAnswerIsHieroglyph,
                            modifier = Modifier.padding(top = if (isPhoneSmallWrong) 0.dp else 4.dp),
                            style = MaterialTheme.typography.bodySmall.copy(
                                fontWeight = FontWeight.Bold,
                                lineHeight = 1.2.em
                            ),
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                        )
                    }
                }
            }
        }
    } else {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
                .semantics { testTag = "feedback_box" },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            MaskoAvatar(
                userData = userData,
                face = face,
                modifier = Modifier
                    .width(avatarWidth)
                    .aspectRatio(1f)
            )
            Spacer(modifier = Modifier.height(8.dp))
            Image(
                modifier = Modifier.width(16.dp),
                painter = painterResource(Res.drawable.triangle),
                contentDescription = null,
                colorFilter = ColorFilter.tint(MaterialTheme.colorScheme.outlineVariant)
            )
            OutlinedCard(
                modifier = Modifier.fillMaxWidth()
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(if (isPhoneSmallWrong) 8.dp else 16.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    Text(
                        modifier = Modifier
                            .semantics { testTag = "feedback_box_text" },
                        text = text,
                        style = MaterialTheme.typography.titleMedium.copy(
                            lineHeight = 1.2.em
                        ),
                        autoSize = TextAutoSize.StepBased(
                            maxFontSize = MaterialTheme.typography.titleMedium.fontSize
                        ),
                        textAlign = TextAlign.Center,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                    if (!correctAnswer.isNullOrBlank()) {
                        CorrectAnswerParenText(
                            correctAnswer = correctAnswer,
                            isHieroglyph = correctAnswerIsHieroglyph,
                            modifier = Modifier
                                .padding(top = if (isPhoneSmallWrong) 0.dp else 4.dp),
                            style = MaterialTheme.typography.bodyMedium.copy(
                                fontWeight = FontWeight.Bold,
                                lineHeight = 1.2.em
                            ),
                            textAlign = TextAlign.Center,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun FeedbackBox(
    character: Characters,
    face: Faces,
    text: String,
    userData: UserData,
    correctAnswer: String? = null,
    correctAnswerIsHieroglyph: Boolean = false,
    isLandscape: Boolean = false,
    useFullText: Boolean = false
) {
    when (character) {
        Characters.Alyn -> {
            AlynFeedbackBox(
                face = face,
                text = text,
                userData = userData,
                correctAnswer = correctAnswer,
                correctAnswerIsHieroglyph = correctAnswerIsHieroglyph,
                isLandscape = isLandscape
            )
        }

        Characters.Nilo -> {
            NiloFeedbackBox(
                text = text,
                userData = userData,
                face = face,
                correctAnswer = correctAnswer,
                correctAnswerIsHieroglyph = correctAnswerIsHieroglyph,
                isLandscape = isLandscape,
                useFullText = useFullText
            )
        }

        Characters.Zeta -> {
            ZetaFeedbackBox(
                face = face,
                text = text,
                userData = userData,
                correctAnswer = correctAnswer,
                correctAnswerIsHieroglyph = correctAnswerIsHieroglyph,
                isLandscape = isLandscape
            )
        }

        Characters.Masko -> {
            MaskoFeedbackBox(
                face = face,
                text = text,
                userData = userData,
                correctAnswer = correctAnswer,
                correctAnswerIsHieroglyph = correctAnswerIsHieroglyph,
                isLandscape = isLandscape
            )
        }
    }
}

fun localizedMeowWord(languageCode: String): String = when (languageCode) {
    "it" -> "Miao"
    "de" -> "Miau"
    "es" -> "Miau"
    "fr" -> "Miaou"
    else -> "Meow"
}
